import Title from "common/Typography/Title";
import { formatDate } from "@shared/utils/date";
import Rate from "common/Rate/Rate";
import styled, { css, useTheme } from "styled-components";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import AntCheckCircleFilled from "@ant-design/icons/CheckCircleFilled";
import AntClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";
import AntCloseCircleFilled from "@ant-design/icons/CloseCircleFilled";
import Button from "common/Buttons/Button";
import { useState } from "react";
import React from "react";
import {
  SubGroupReview,
  SubGroupReviewStatus,
  LanguageNameSpaces,
  SubGroupReviewRejectionReason,
} from "@shared/types";
import Flex from "common/Boxes/Flex";
import Link from "common/Typography/Link";
import IntercomLink from "common/Text/IntercomLink";
import { MARKETPLACE_REVIEW_GUIDELINES_ARTICLE } from "@shared/features/newMarketplace/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { getRejectedReviewMessageContent } from "@shared/features/newMarketplace/utils.ts";
import NewLookCurrentUserReview from "features/newMarketplace/GroupExtraDetails/Tabs/ReviewsTab/NewLookCurrentUserReview";

interface CurrentUserReviewProps {
  currentUserReview: SubGroupReview;
  isEdit: boolean;
  onEdit: () => void;
}

const CurrentUserReview = ({
  currentUserReview,
  isEdit,
  onEdit,
}: CurrentUserReviewProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const appTheme = useTheme();
  const [visible, setVisible] = useState(true);

  if (appTheme.isNewLook) {
    return (
      <NewLookCurrentUserReview
        currentUserReview={currentUserReview}
        isEdit={isEdit}
        onEdit={onEdit}
      />
    );
  }

  const editReview = () => {
    onEdit();
    setVisible(false);
  };

  const addCheckingReviewMessage = () => {
    return (
      <Message>
        <ClockCircleOutlined $isMobile={isMobile} />
        <Title type={"secondary"} level={isMobile ? 5 : 4}>
          {t("reviews.checking_review")}
        </Title>
      </Message>
    );
  };

  const addApprovedReviewMessage = () => {
    return (
      <Message>
        <CheckCircleFilled $isMobile={isMobile} />
        <Title type={"secondary"} level={isMobile ? 5 : 4}>
          {t("reviews.review_approved")}
        </Title>
      </Message>
    );
  };

  const addRejectedReviewMessage = () => {
    if (visible) {
      return (
        <Message style={{ flexDirection: "column" }}>
          <Flex>
            <CloseCircleFilled $isMobile={isMobile} />
            <Title type={"secondary"} level={isMobile ? 5 : 4}>
              {getRejectedReviewMessageContent(
                currentUserReview.rejection_reason,
                t
              )}
              <IntercomLink
                articleId={MARKETPLACE_REVIEW_GUIDELINES_ARTICLE}
                style={{
                  marginLeft: 6,
                  whiteSpace: "nowrap",
                  fontSize: "inherit",
                }}
              >
                {t("reviews.review_guidelines_link")}
              </IntercomLink>
            </Title>
          </Flex>
          <Flex $justifyContent="center" style={{ gridGap: 8, marginTop: 16 }}>
            <Button
              type="text"
              style={{ color: appTheme.colors.lightestText, fontWeight: 600 }}
              onClick={() => setVisible(false)}
            >
              {t("reviews.dismiss")}
            </Button>
            <Button
              type="text"
              style={{ color: appTheme.colors.primaryBlue, fontWeight: 600 }}
              onClick={editReview}
            >
              {t("reviews.edit_review_button")}
            </Button>
          </Flex>
        </Message>
      );
    }
  };

  const isUserReviewPending =
    currentUserReview.status === SubGroupReviewStatus.PENDING;
  const isUserReviewApproved =
    currentUserReview.status === SubGroupReviewStatus.APPROVED;
  const isUserReviewRejected =
    currentUserReview.status === SubGroupReviewStatus.REJECTED;
  const isUserReviewDeactivated =
    currentUserReview.status === SubGroupReviewStatus.DEACTIVATED;

  const isUserReviewFadeOut = isUserReviewPending || isUserReviewRejected;
  const showCurrentUserReview = !isEdit && !isUserReviewDeactivated;

  return (
    <React.Fragment>
      {isUserReviewPending && addCheckingReviewMessage()}
      {isUserReviewApproved &&
        !currentUserReview.approved_viewed &&
        addApprovedReviewMessage()}
      {isUserReviewRejected && addRejectedReviewMessage()}
      {showCurrentUserReview && (
        <UserReviewWrapper $isMobile={isMobile}>
          <UserReview $fadeOut={isUserReviewFadeOut}>
            <Title type={"secondary"} level={4} style={{ marginBottom: 16 }}>
              {t("reviews.your_review")}
            </Title>
            <Flex $alignItems="flex-start">
              <div>
                <UserName level={isMobile ? 3 : 2}>
                  {currentUserReview.full_name}
                </UserName>
                <ReviewCreationDate level={4}>
                  {formatDate(
                    currentUserReview.creation_timestamp,
                    false,
                    true
                  )}
                </ReviewCreationDate>
              </div>
              <Rate
                disabled
                value={currentUserReview.stars}
                style={{ fontSize: isMobile ? 16 : 20 }}
              />
            </Flex>
            <Title type={"secondary"} level={4}>
              {currentUserReview.message}
            </Title>
          </UserReview>
          {(isUserReviewApproved || isUserReviewPending) && (
            <EditReview onClick={editReview}>{t("reviews.edit")}</EditReview>
          )}
        </UserReviewWrapper>
      )}
    </React.Fragment>
  );
};

export default CurrentUserReview;

const Message = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  text-align: center;
  margin-bottom: 16px;
  justify-content: center;
`;
const UserName = styled(Title)`
  font-weight: 600;
`;
const ReviewCreationDate = styled(Title)`
  color: ${(p) => p.theme.colors.lightestGrey};
  margin-bottom: 16px;
`;
const IconStyle = css<{ $isMobile: boolean }>`
  font-size: ${(p) => (p.$isMobile ? 24 : 32)}px;
  margin-right: 16px;
`;
const ClockCircleOutlined = styled(AntClockCircleOutlined)<{
  $isMobile: boolean;
}>`
  color: ${(p) => p.theme.colors.secondaryGrey};
  ${IconStyle}
`;
const CheckCircleFilled = styled(AntCheckCircleFilled)<{ $isMobile: boolean }>`
  color: ${(p) => p.theme.colors.lightGreen};
  ${IconStyle}
`;
const CloseCircleFilled = styled(AntCloseCircleFilled)<{ $isMobile: boolean }>`
  color: ${(p) => p.theme.colors.red};
  ${IconStyle}
`;
const UserReview = styled.div<{ $fadeOut: boolean }>`
  ${(p) =>
    p.$fadeOut &&
    `
    filter: opacity(50%);
  `}
`;
const UserReviewWrapper = styled.div<{ $isMobile: boolean }>`
  padding: ${(p) => (p.$isMobile ? "0px 18px 18px" : "0 24px 24px")};
`;
const EditReview = styled(Link)`
  display: flex;
  justify-content: end;
`;
